import { SERVER_URL } from "../../../../utils/serverConfig";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

/**
 * Export one Work Summary surface as a branded PDF (2026-08-30).
 *
 *   exportWorkSummary({ kind: "day" | "hourly" | "department",
 *                       userId?, department?, date, source })
 *
 * Always downloads a .pdf. Server prints via headless Chrome when it can;
 * otherwise this file rasterises the same HTML into a multi-page PDF here.
 */

export async function htmlToPdfBlob(html) {
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText = "position:fixed;left:0;top:0;width:794px;height:200px;opacity:0;border:0;pointer-events:none;z-index:-1;";
  document.body.appendChild(iframe);
  await new Promise((resolve, reject) => {
    iframe.onload = () => resolve();
    iframe.onerror = () => reject(new Error("Could not render the report."));
    iframe.srcdoc = html;
  });
  const doc = iframe.contentDocument;
  const body = doc && doc.body;
  if (!body) {
    iframe.remove();
    throw new Error("Could not render the report.");
  }
  const fullH = Math.max(body.scrollHeight, doc.documentElement.scrollHeight, 400);
  iframe.style.height = `${fullH}px`;
  if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
  await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
  try {
    const canvas = await html2canvas(body, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      windowWidth: 794,
      height: fullH,
      logging: false,
    });
    const img = canvas.toDataURL("image/jpeg", 0.95);
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const imgH = canvas.height * (pageW / canvas.width);
    let heightLeft = imgH;
    let y = 0;
    pdf.addImage(img, "JPEG", 0, y, pageW, imgH);
    heightLeft -= pageH;
    while (heightLeft > 0) {
      y -= pageH;
      pdf.addPage();
      pdf.addImage(img, "JPEG", 0, y, pageW, imgH);
      heightLeft -= pageH;
    }
    return pdf.output("blob");
  } finally {
    iframe.remove();
  }
}

function filenameFor({ kind, label, department, date }) {
  const who = String(label || department || "member").replace(/[^\w.-]+/g, "_");
  return `teamgrid-${kind}-${who}-${date}.pdf`;
}

export async function exportWorkSummary({ kind, userId, department, date, source = "both", label }) {
  const token = localStorage.getItem("token");
  const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/export`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
    body: JSON.stringify({ kind, userId, department, date, source }),
  });
  if (!res.ok) {
    let msg = "Could not build the export.";
    try { const j = await res.json(); msg = j?.message || msg; } catch (_) { /* not json */ }
    throw new Error(msg);
  }
  const raw = await res.blob();
  const buf = await raw.arrayBuffer();
  const magic = new Uint8Array(buf.slice(0, 4));
  const looksPdf = magic[0] === 0x25 && magic[1] === 0x50 && magic[2] === 0x44 && magic[3] === 0x46; // %PDF
  const type = (res.headers.get("Content-Type") || raw.type || "").toLowerCase();
  const isPdf = looksPdf || type.includes("pdf");
  const blob = isPdf
    ? new Blob([buf], { type: "application/pdf" })
    : await htmlToPdfBlob(new TextDecoder().decode(buf));
  const filename = filenameFor({ kind, label, department, date });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.style.display = "none";
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1500);
  return filename;
}

function saveBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1500);
  return filename;
}

export async function downloadHtmlAsPdf(html, filename) {
  const name = filename.endsWith(".pdf") ? filename : `${filename}.pdf`;
  try {
    const token = localStorage.getItem("token");
    const res = await fetch(`${SERVER_URL}/api/v1/work-summary-new/day-overview-pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ html, filename: name }),
    });
    if (res.ok) {
      const buf = await res.arrayBuffer();
      const magic = new Uint8Array(buf.slice(0, 4));
      const looksPdf = magic[0] === 0x25 && magic[1] === 0x50 && magic[2] === 0x44 && magic[3] === 0x46;
      if (looksPdf) return saveBlob(new Blob([buf], { type: "application/pdf" }), name);
    }
  } catch (_) { /* fall through to on-page print */ }
  const blob = await htmlToPdfBlob(html);
  return saveBlob(blob, name);
}
